import React, { useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RestService from '../Services/RestService';
import OKomLoading from '../Components/OsloKommuneUI/OKomLoading';
import PktAlert from '../Components/PktAlert';
import { useUserContext } from '../Components/StateContext/UserContext';

const CLASS_OPTIONS = Array.from({ length: 13 }, (_, i) => `${i + 1}.klasse`);
const MAX_CHILDREN = 5;

const DOB_REGEX = /^(0[1-9]|[12]\d|3[01])\.(0[1-9]|1[0-2])\.(\d{4})$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NO_MOBILE_REGEX = /^[49]\d{7}$/;

const validateChild = (child: Api.ExamRegistrationChild, label: string): string | null => {
  if (!child.Firstname.trim()) return `Fornavn er påkrevd for ${label}`;
  if (!child.Lastname.trim()) return `Etternavn er påkrevd for ${label}`;
  if (!child.FirstnameTamil?.trim()) return `Fornavn (tamilsk) er påkrevd for ${label}. Dette kommer på diplom/sertifikat, så det er viktig å stave riktig!`;
  if (!child.LastnameTamil?.trim()) return `Etternavn (tamilsk) er påkrevd for ${label}. Dette kommer på diplom/sertifikat, så det er viktig å stave riktig!`;
  if (!child.DateOfBirth?.trim()) return `Fødselsdato er påkrevd for ${label}`;
  if (!DOB_REGEX.test(child.DateOfBirth.trim())) return `Fødselsdato må være på format dd.mm.yyyy for eksempel 25.03.2012 (${label})`;
  const [day, month, year] = child.DateOfBirth.trim().split('.').map(Number);
  const dob = new Date(year, month - 1, day);
  if (dob.getFullYear() !== year || dob.getMonth() !== month - 1 || dob.getDate() !== day) return `Fødselsdato finnes ikke (${label})`;
  if (!child.DepartmentId || child.DepartmentId <= 0) return `Avdeling er påkrevd for ${label}`;
  if (!child.ClassName) return `Klasse er påkrevd for ${label}`;
  return null;
};

const validateForm = (form: Api.ExamRegistrationRequest): string | null => {
  if (!form.Children || form.Children.length === 0) return 'Minst ett barn må registreres';
  for (let i = 0; i < form.Children.length; i++) {
    const err = validateChild(form.Children[i], form.Children.length > 1 ? `barn ${i + 1}` : 'barnet');
    if (err) return err;
  }
  if (!form.ParentEmail.trim()) return 'Forelder e-post er påkrevd';
  if (!EMAIL_REGEX.test(form.ParentEmail.trim())) return 'Ugyldig e-postadresse';
  if (!form.ParentMobile?.trim()) return 'Forelder mobilnummer er påkrevd';
  if (!NO_MOBILE_REGEX.test(form.ParentMobile.trim())) return 'Mobilnummer må være 8 siffer og starte med 4 eller 9 (uten +47)';
  const hasParent2 = !!(form.Parent2Email?.trim() || form.Parent2Mobile?.trim());
  if (hasParent2) {
    if (!form.Parent2Email?.trim()) return 'Forelder 2 e-post er påkrevd';
    if (!EMAIL_REGEX.test(form.Parent2Email.trim())) return 'Ugyldig e-postadresse for forelder 2';
    if (!form.Parent2Mobile?.trim()) return 'Forelder 2 mobilnummer er påkrevd';
    if (!NO_MOBILE_REGEX.test(form.Parent2Mobile.trim())) return 'Mobilnummer for forelder 2 må være 8 siffer og starte med 4 eller 9 (uten +47)';
  }
  return null;
};

const emptyChild = (): Api.ExamRegistrationChild => ({
  Firstname: '',
  Lastname: '',
  FirstnameTamil: '',
  LastnameTamil: '',
  DateOfBirth: '',
  DepartmentId: 0,
  ClassName: '',
});

const emptyForm = (): Api.ExamRegistrationRequest => ({
  Children: [emptyChild()],
  ParentEmail: '',
  ParentMobile: '',
  Parent2Email: '',
  Parent2Mobile: '',
});

const removeButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: '1.5rem',
  lineHeight: 1,
  padding: '0 .25rem',
};

const formatDob = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()}`;
};

const ExamRegistrationView: React.FC<RouteComponentProps> = () => {
  const { userToken, userRoles } = useUserContext();
  const isAuthenticated = !!userToken;
  const [isLoading, setIsLoading] = useState(false);
  const [departments, setDepartments] = useState<Api.Department[]>([]);
  const [form, setForm] = useState<Api.ExamRegistrationRequest>(emptyForm());
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showParent2, setShowParent2] = useState(false);
  const [submittedData, setSubmittedData] = useState<Api.ExamRegistrationRequest | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await RestService.get<Api.Department[]>('/api/examregistration/GetDepartments');
        setDepartments(res);
      } catch (e) {
        toast.error('Kunne ikke laste avdelinger');
      }
    })();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const rows = await RestService.get<Api.ExamRegistration[]>('/api/examregistration/MyRegistrations');
        if (rows && rows.length > 0) {
          const first = rows[0];
          setForm({
            Children: rows.map(r => ({
              Firstname: r.Firstname ?? '',
              Lastname: r.Lastname ?? '',
              FirstnameTamil: r.FirstnameTamil ?? '',
              LastnameTamil: r.LastnameTamil ?? '',
              DateOfBirth: formatDob(r.DateOfBirth),
              DepartmentId: r.DepartmentId ?? 0,
              ClassName: r.ClassName ?? '',
            })),
            ParentEmail: first.ParentEmail ?? '',
            ParentMobile: first.ParentMobile ?? '',
            Parent2Email: first.Parent2Email ?? '',
            Parent2Mobile: first.Parent2Mobile ?? '',
          });
          setShowParent2(!!first.Parent2Email);
          setIsEditMode(true);
        }
      } catch {
        // user has no existing registrations or fetch failed — silently leave form blank
      }
    })();
  }, [isAuthenticated]);

  const updateParent = <K extends keyof Api.ExamRegistrationRequest>(field: K, value: Api.ExamRegistrationRequest[K]) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setValidationError(null);
  };

  const updateChild = <K extends keyof Api.ExamRegistrationChild>(index: number, field: K, value: Api.ExamRegistrationChild[K]) => {
    setForm(prev => ({
      ...prev,
      Children: prev.Children.map((c, i) => (i === index ? { ...c, [field]: value } : c)),
    }));
    setValidationError(null);
  };

  const addChild = () => {
    setForm(prev => (prev.Children.length >= MAX_CHILDREN ? prev : { ...prev, Children: [...prev.Children, emptyChild()] }));
    setValidationError(null);
  };

  const removeChild = (index: number) => {
    setForm(prev => ({ ...prev, Children: prev.Children.filter((_, i) => i !== index) }));
    setValidationError(null);
  };

  const addParent2 = () => setShowParent2(true);
  const removeParent2 = () => {
    setShowParent2(false);
    setForm(prev => ({ ...prev, Parent2Email: '', Parent2Mobile: '' }));
    setValidationError(null);
  };

  const translateNameField = async (
    index: number,
    sourceField: 'Firstname' | 'Lastname',
    targetField: 'FirstnameTamil' | 'LastnameTamil',
  ) => {
    const child = form.Children[index];
    if (!child) return;
    const english = (child[sourceField] ?? '').trim();
    if (!english) return;
    if ((child[targetField] ?? '').trim()) return;
    try {
      const res = await RestService.post<Api.TranslateNameRequest, Api.TranslateNameResponse>(
        '/api/examregistration/TranslateName',
        { EnglishName: english },
      );
      if (res?.Tamil) {
        setForm(prev => ({
          ...prev,
          Children: prev.Children.map((c, i) =>
            i === index && !(c[targetField] ?? '').trim() ? { ...c, [targetField]: res.Tamil } : c,
          ),
        }));
      }
    } catch {
      // best-effort; user can type manually
    }
  };

  const handleSubmit = async () => {
    const error = validateForm(form);
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError(null);
    try {
      setIsLoading(true);
      const endpoint = isAuthenticated
        ? '/api/examregistration/UpdateMyRegistrations'
        : '/api/examregistration/Register';
      const response = await RestService.post<Api.ExamRegistrationRequest, Api.LoginResponse>(endpoint, form);
      if (response.Success) {
        const snapshot: Api.ExamRegistrationRequest = JSON.parse(JSON.stringify(form));
        setSubmittedData(snapshot);
        setForm(emptyForm());
        setShowParent2(false);
      } else {
        toast.error(response.Message ?? 'Noe gikk galt');
      }
    } catch (e) {
      toast.error(`Noe gikk galt: ${e}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (submittedData) {
    const departmentName = (id?: number) => departments.find(d => d.Id === id)?.Name ?? '';
    return (
      <div className="ods-margin-top-25 ods-grid ods-grid--gap ods-grid--center">
        <div className="ods-grid__column--12 ods-grid__column--start-3-breakpoint-medium ods-grid__column--end-11-breakpoint-medium">
          <div className="ods-status-card">
            <div className="ods-status-card__content">
              <h3 className="ods-status-card__heading">TAKK FOR PÅMELDINGEN</h3>
              <p>Vi har registrert påmeldingen din.</p>

              <h4 className="ods-margin-top-11">Oppsummering av registrering:</h4>

              <p className="ods-margin-top-3"><strong>Barn:</strong></p>
              <ul>
                {submittedData.Children.map((c, i) => (
                  <li key={i}>
                    {c.Firstname} {c.Lastname}
                    {c.FirstnameTamil || c.LastnameTamil ? ` (${[c.FirstnameTamil, c.LastnameTamil].filter(Boolean).join(' ')})` : ''}
                    {' — '}f. {c.DateOfBirth}, {departmentName(c.DepartmentId)}, {c.ClassName}
                  </li>
                ))}
              </ul>

              <p className="ods-margin-top-3"><strong>Foreldre:</strong></p>
              <ul>
                <li>{submittedData.ParentEmail} — {submittedData.ParentMobile}</li>
                {submittedData.Parent2Email && (
                  <li>{submittedData.Parent2Email} — {submittedData.Parent2Mobile}</li>
                )}
              </ul>

              <p className="ods-margin-top-11">Du vil også få en bekreftelse på epost.</p>

              <p className="ods-margin-top-11">
                Med vennlig hilsen<br />
                Annaipoopathi aktivitets- og kultursenter (APTAK)
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {isLoading && <OKomLoading text='Lagrer, vennligst vent...' />}
      <div className="ods-margin-top-25 ods-grid ods-grid--gap ods-grid--center">
        <div className="ods-grid__column--12 ods-grid__column--start-2-breakpoint-medium ods-grid__column--end-7-breakpoint-medium">
          {form.Children.map((child, index) => (
            <div key={index} className={index === 0 ? 'ods-status-card' : 'ods-status-card ods-margin-top-3'}>
              <div className="ods-status-card__content">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 className="ods-status-card__heading">{form.Children.length > 1 ? `OM BARNET ${index + 1}` : 'OM BARNET'}</h3>
                  {index > 0 && (
                    <button type="button" onClick={() => removeChild(index)} aria-label={`Fjern barn ${index + 1}`} style={removeButtonStyle}>×</button>
                  )}
                </div>

                <label className="ods-input__label">Fornavn
                  <input value={child.Firstname} onChange={e => updateChild(index, 'Firstname', e.target.value)} onBlur={() => translateNameField(index, 'Firstname', 'FirstnameTamil')} className="ods-input__input" type="text" autoComplete="off" />
                </label>

                <label className="ods-input__label">Etternavn
                  <input value={child.Lastname} onChange={e => updateChild(index, 'Lastname', e.target.value)} onBlur={() => translateNameField(index, 'Lastname', 'LastnameTamil')} className="ods-input__input" type="text" autoComplete="off" />
                </label>

                <label className="ods-input__label ods-margin-top-11">Fornavn (tamilsk) - NB! Kommer på diplomet
                  <input value={child.FirstnameTamil ?? ''} onChange={e => updateChild(index, 'FirstnameTamil', e.target.value)} className="ods-input__input" type="text" />
                </label>

                <label className="ods-input__label">Etternavn (tamilsk) - NB! Kommer på diplomet
                  <input value={child.LastnameTamil ?? ''} onChange={e => updateChild(index, 'LastnameTamil', e.target.value)} className="ods-input__input" type="text" />
                </label>

                <label className="ods-input__label ods-margin-top-11">Fødselsdato (dd.mm.yyyy)
                  <input value={child.DateOfBirth ?? ''} onChange={e => updateChild(index, 'DateOfBirth', e.target.value)} className="ods-input__input" type="text" placeholder="dd.mm.yyyy" />
                </label>

                <label className="ods-input__label">Avdeling
                  <select value={child.DepartmentId} onChange={e => updateChild(index, 'DepartmentId', parseInt(e.target.value, 10))} className="ods-input__input">
                    <option value={0}>-- velg avdeling --</option>
                    {departments.map(d => (<option key={d.Id} value={d.Id}>{d.Name}</option>))}
                  </select>
                </label>

                <label className="ods-input__label">Klasse
                  <select value={child.ClassName} onChange={e => updateChild(index, 'ClassName', e.target.value)} className="ods-input__input">
                    <option value="">-- velg klasse --</option>
                    {CLASS_OPTIONS.map(c => (<option key={c} value={c}>{c}</option>))}
                  </select>
                </label>
              </div>
            </div>
          ))}

          {form.Children.length < MAX_CHILDREN && (
            <button type="button" onClick={addChild} className="ods-button ods-button--yellow ods-button--fullwidth ods-margin-top-3">+ Legg til barn</button>
          )}
        </div>

        <div className="ods-grid__column--12 ods-grid__column--start-7-breakpoint-medium ods-grid__column--end-12-breakpoint-medium">
          <div className="ods-status-card">
            <div className="ods-status-card__content">
              <h3 className="ods-status-card__heading">OM FORELDER</h3>

              <label className="ods-input__label">Forelder e-post
                <input value={form.ParentEmail} onChange={e => updateParent('ParentEmail', e.target.value)} className="ods-input__input" type="email" autoComplete="email" />
              </label>

              <label className="ods-input__label">Forelder mobilnummer
                <input value={form.ParentMobile ?? ''} onChange={e => updateParent('ParentMobile', e.target.value)} className="ods-input__input" type="tel" autoComplete="tel" />
              </label>
            </div>
          </div>

          {showParent2 ? (
            <div className="ods-status-card ods-margin-top-3">
              <div className="ods-status-card__content">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 className="ods-status-card__heading">OM FORELDER 2</h3>
                  <button type="button" onClick={removeParent2} aria-label="Fjern forelder 2" style={removeButtonStyle}>×</button>
                </div>

                <label className="ods-input__label">Forelder e-post
                  <input value={form.Parent2Email ?? ''} onChange={e => updateParent('Parent2Email', e.target.value)} className="ods-input__input" type="email" autoComplete="off" />
                </label>

                <label className="ods-input__label">Forelder mobilnummer
                  <input value={form.Parent2Mobile ?? ''} onChange={e => updateParent('Parent2Mobile', e.target.value)} className="ods-input__input" type="tel" autoComplete="off" />
                </label>
              </div>
            </div>
          ) : (
            <button type="button" onClick={addParent2} className="ods-button ods-button--yellow ods-button--fullwidth ods-margin-top-3">+ Legg til forelder</button>
          )}
        </div>

        <div className="ods-grid__column--12 ods-grid__column--start-4-breakpoint-medium ods-grid__column--end-10-breakpoint-medium ods-margin-top-11">
          {validationError && (
            <div className="ods-margin-bottom-3">
              <PktAlert title="Mangler informasjon" skin="warning" compact>
                <span>{validationError}</span>
              </PktAlert>
            </div>
          )}
          <button disabled={isLoading} className="ods-button ods-button--fullwidth" onClick={handleSubmit}>Send</button>
        </div>

      </div>
    </>
  );
};

export default withRouter(ExamRegistrationView);
